import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import {
  TextField,
  Button,
  Paper,
  Box,
  Typography,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";

export default function EditInvoicePage() {
  useProtectedRoute();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const { token } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState<any>(null);
  const [form, setForm] = useState<any>({
    clientId: "",
    items: [],
    notes: "",
  });
  const [error, setError] = useState<string | null>(null);

  const surfaceBorder = `1px solid ${theme.palette.divider}`;
  const tableHeaderBg = alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.08);
  const tableStyle: CSSProperties = {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 720,
  };
  const headerCellStyle: CSSProperties = {
    padding: theme.spacing(1.5),
    fontWeight: 600,
    borderBottom: surfaceBorder,
    borderRight: surfaceBorder,
    textAlign: "left",
    backgroundColor: tableHeaderBg,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
  };
  const cellStyle: CSSProperties = {
    padding: theme.spacing(1),
    borderBottom: surfaceBorder,
    borderRight: surfaceBorder,
    verticalAlign: "top",
    backgroundColor: theme.palette.background.paper,
  };
  const numericCellStyle: CSSProperties = {
    ...cellStyle,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  };

  useEffect(() => {
    async function fetchInvoice() {
      try {
        setLoading(true);
        const res = await api.get(`/invoices/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setInvoice(res.data);
        setForm({
          clientId: res.data.clientId?._id || res.data.clientId,
          items: res.data.items || [],
          notes: res.data.notes || "",
        });
      } catch (err) {
        setError("Failed to load invoice");
      } finally {
        setLoading(false);
      }
    }
    fetchInvoice();
  }, [id, token]);

  const updateField = (field: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [field]: value }));
  };

  const updateItem = (index: number, key: string, value: any) => {
    setForm((prev: any) => {
      const items = [...prev.items];
      items[index][key] = value;
      return { ...prev, items };
    });
  };

  const addItem = () => {
    setForm((prev: any) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          type: "service",
          name: "",
          description: "",
          quantity: 1,
          costPrice: 0,
          sellingPrice: 0,
          taxRate: 0,
          discount: 0,
          lineTotal: 0,
        },
      ],
    }));
  };

  const removeItem = (index: number) => {
    setForm((prev: any) => {
      const items = [...prev.items];
      items.splice(index, 1);
      return { ...prev, items };
    });
  };

  const calcTotals = () => {
    let subtotal = 0;
    let taxTotal = 0;
    let discountTotal = 0;
    form.items.forEach((li: any) => {
      const itemTotal = li.sellingPrice * li.quantity;
      const itemTax = itemTotal * (li.taxRate / 100);
      subtotal += itemTotal;
      taxTotal += itemTax;
      discountTotal += li.discount;
    });
    const grandTotal = subtotal + taxTotal - discountTotal;
    return { subtotal, taxTotal, discountTotal, grandTotal };
  };

  const { subtotal, taxTotal, discountTotal, grandTotal } = calcTotals();

  const handleSave = async (e: any) => {
    e.preventDefault();
    try {
      await api.put(
        `/invoices/${id}`,
        {
          ...form,
          items: form.items.map((li: any) => ({
            ...li,
            lineTotal: li.sellingPrice * li.quantity - li.discount,
          })),
          subtotal,
          taxTotal,
          discountTotal,
          grandTotal,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      alert("Invoice updated!");
      navigate(`/invoices/${id}`);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to update invoice");
    }
  };

  if (loading)
    return (
      <Box p={6} textAlign="center">
        <Typography color="text.secondary">Loading invoice�</Typography>
      </Box>
    );

  if (error)
    return (
      <Box p={6} textAlign="center">
        <Typography color="error">{error}</Typography>
      </Box>
    );

  return (
    <Paper
      elevation={theme.palette.mode === "dark" ? 0 : 2}
      sx={{
        maxWidth: 960,
        mx: "auto",
        mt: { xs: 2.5, md: 6 },
        p: { xs: 2.5, md: 5 },
        borderRadius: 3,
        backgroundColor: theme.palette.background.paper,
        border: surfaceBorder,
      }}
    >
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        gap={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        mb={3}
      >
        <Box>
          <Typography variant={isSmDown ? "h5" : "h4"} fontWeight={800} gutterBottom>
            Edit Invoice #{invoice?.invoiceNumber}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Update line items and notes. Changes reflect after saving.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate(-1)} size={isSmDown ? "small" : "medium"}>
          Back
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <form onSubmit={handleSave}>
        <Box mb={3}>
          <Typography component="label" variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: "block" }}>
            Notes
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={form.notes}
            onChange={(e) => updateField("notes", e.target.value)}
          />
        </Box>

        <Box mb={3}>
          <Typography component="label" variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: "block" }}>
            Line Items
          </Typography>
          <Box
            sx={{
              overflowX: "auto",
              borderRadius: 2,
              border: surfaceBorder,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={headerCellStyle}>Type</th>
                  <th style={headerCellStyle}>Name</th>
                  <th style={headerCellStyle}>Description</th>
                  <th style={headerCellStyle}>Qty</th>
                  <th style={headerCellStyle}>Unit Price</th>
                  <th style={headerCellStyle}>Tax %</th>
                  <th style={headerCellStyle}>Discount</th>
                  <th style={headerCellStyle}>Total</th>
                  <th style={headerCellStyle}></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((li: any, idx: number) => (
                  <tr key={idx}>
                    <td style={cellStyle}>
                      <select
                        value={li.type}
                        onChange={(e) => updateItem(idx, "type", e.target.value)}
                        style={{
                          width: "100%",
                          padding: theme.spacing(0.75),
                          borderRadius: theme.shape.borderRadius,
                          border: `1px solid ${theme.palette.divider}`,
                          backgroundColor: theme.palette.background.default,
                          color: theme.palette.text.primary,
                        }}
                      >
                        <option value="service">Service</option>
                        <option value="part">Part</option>
                        <option value="labor">Labor</option>
                        <option value="accessory">Accessory</option>
                        <option value="diagnostic fee">Diagnostic</option>
                        <option value="software">Software</option>
                        <option value="custom">Custom</option>
                      </select>
                    </td>
                    <td style={cellStyle}>
                      <TextField
                        size="small"
                        value={li.name}
                        onChange={(e) => updateItem(idx, "name", e.target.value)}
                        fullWidth
                      />
                    </td>
                    <td style={cellStyle}>
                      <TextField
                        size="small"
                        value={li.description}
                        onChange={(e) => updateItem(idx, "description", e.target.value)}
                        fullWidth
                      />
                    </td>
                    <td style={cellStyle}>
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 1 }}
                        value={li.quantity}
                        onChange={(e) => updateItem(idx, "quantity", Number(e.target.value))}
                        fullWidth
                      />
                    </td>
                    <td style={cellStyle}>
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        value={li.sellingPrice}
                        onChange={(e) => updateItem(idx, "sellingPrice", Number(e.target.value))}
                        fullWidth
                      />
                    </td>
                    <td style={cellStyle}>
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                        value={li.taxRate}
                        onChange={(e) => updateItem(idx, "taxRate", Number(e.target.value))}
                        fullWidth
                      />
                    </td>
                    <td style={cellStyle}>
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        value={li.discount}
                        onChange={(e) => updateItem(idx, "discount", Number(e.target.value))}
                        fullWidth
                      />
                    </td>
                    <td style={numericCellStyle}>
                      ${(li.sellingPrice * li.quantity - li.discount).toFixed(2)}
                    </td>
                    <td style={{ ...cellStyle, borderRight: "none", textAlign: "center" }}>
                      <Button color="error" size="small" onClick={() => removeItem(idx)}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={9} style={{ ...cellStyle, borderRight: "none", textAlign: "right" }}>
                    <Button type="button" variant="contained" onClick={addItem}>
                      + Add Line Item
                    </Button>
                  </td>
                </tr>
              </tbody>
            </table>
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            maxWidth: isSmDown ? "100%" : 360,
            ml: isSmDown ? 0 : "auto",
            gap: 0.75,
            mt: 6,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "right" }}>
            Subtotal:
          </Typography>
          <Typography variant="body2" sx={{ textAlign: "right" }}>
            ${subtotal.toFixed(2)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "right" }}>
            Tax:
          </Typography>
          <Typography variant="body2" sx={{ textAlign: "right" }}>
            ${taxTotal.toFixed(2)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "right" }}>
            Discount:
          </Typography>
          <Typography variant="body2" sx={{ textAlign: "right" }}>
            -${discountTotal.toFixed(2)}
          </Typography>
          <Typography variant="h6" sx={{ textAlign: "right" }}>
            Grand Total:
          </Typography>
          <Typography variant="h6" sx={{ textAlign: "right", color: theme.palette.primary.main }}>
            ${grandTotal.toFixed(2)}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          gap={2}
          justifyContent="flex-end"
        >
          <Button type="button" variant="outlined" onClick={() => navigate(-1)} fullWidth={isSmDown}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="success" fullWidth={isSmDown}>
            Save Changes
          </Button>
        </Box>
      </form>
    </Paper>
  );
}
